import { useEffect, useState } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";

import { acquireApiToken } from "@/auth/acquireApiToken";
import { getAdminApiUrl, Env } from "../shared";

/**
 * Runtime feature flags for the admin site.
 *
 * These used to be `REACT_APP_*` build-time constants, which meant the same flag
 * existed twice — in AppConfig for the backend, and baked into the bundle for the
 * UI — and flipping it needed a rebuild and redeploy. `GET /admin/feature-flags`
 * now returns the AppConfig values the backend itself reads, so both sides agree
 * and a flip takes effect on the next page load.
 *
 * `recurring-deals-enabled` gates the weekly-recurrence UI:
 *   - "Run Scheduler" and "Occurrences" buttons on the DealsTab toolbar
 *   - 🔁 Recurring badge (and its occurrence drill-down) on source deals
 *   - Repeats section in the deal create form and EditDealModal
 *
 * Fail-closed: flags read false until the fetch resolves, and stay false if it
 * fails. An outage hides the recurrence UI rather than showing controls whose
 * backend is switched off.
 */

export type AdminFlagName =
  | "recurring-deals-enabled"
  | "deals-enabled"
  | "cover-enabled"
  | "gamification_enabled"
  | "referral_codes_enabled";

const DEFAULT_FLAGS: Record<AdminFlagName, boolean> = {
  "recurring-deals-enabled": false,
  "deals-enabled": false,
  "cover-enabled": false,
  gamification_enabled: false,
  referral_codes_enabled: false,
};

// One in-flight request per environment, shared across every consumer, so a page
// with several flag-gated components makes one call rather than one each.
const cache = new Map<Env, Promise<Record<string, boolean>>>();

async function fetchFlags(
  env: Env,
  token: string | null,
): Promise<Record<string, boolean>> {
  const base = getAdminApiUrl(env);
  if (!base) return {};
  const resp = await fetch(`${base}/admin/feature-flags`, {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!resp.ok) throw new Error(`GET /admin/feature-flags returned HTTP ${resp.status}`);
  const data = await resp.json();
  return (data?.flags ?? {}) as Record<string, boolean>;
}

/** Read one runtime flag. False until the real value arrives. */
export function useAdminFlag(name: AdminFlagName, env: Env = "prod"): boolean {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const [value, setValue] = useState<boolean>(DEFAULT_FLAGS[name]);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      let pending = cache.get(env);
      if (!pending) {
        pending = (async () => {
          let token: string | null = null;
          if (isAuthenticated && accounts.length > 0) {
            token = await acquireApiToken(instance, accounts[0]);
          }
          return fetchFlags(env, token);
        })().catch((err) => {
          // Loudly, on purpose. Failing closed means the UI quietly disappears,
          // which is the exact failure this endpoint exists to prevent — so the
          // reason has to be visible rather than inferred from missing buttons.
          console.error(
            `[featureFlags] could not load ${env} flags — admin UI gated on them ` +
              `will stay hidden until this succeeds:`,
            err,
          );
          return {} as Record<string, boolean>;
        });
        cache.set(env, pending);
      }
      const flags = await pending;
      if (alive) setValue(Boolean(flags[name] ?? DEFAULT_FLAGS[name]));
    };

    void load();
    return () => {
      alive = false;
    };
    // `accounts` is a new array each render; its length is the meaningful change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, env, isAuthenticated, instance, accounts.length]);

  return value;
}

/** Drop cached responses so the next read re-fetches — call after flipping a flag. */
export function clearAdminFlagCache(): void {
  cache.clear();
}
